import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DollarSign, Filter, Inbox, MapPin, MessageCircle, Quote, Search, SearchX, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useWhatsApp } from "@/lib/siteSettings";
import { ISTANBUL_AREAS } from "@/data/istanbulAreas";
import { EmptyState } from "@/components/EmptyState";
import { ListingGridSkeleton } from "@/components/skeletons";
import { DateFilterPills, periodSince, type Period } from "@/components/DateFilterPills";
import {
  apartmentTypeOptions, currencyOptions, formatApartmentType, formatListingType, formatRequestListingType, formatPrice, listingTypeOptions,
  type ApartmentType, type CurrencyCode, type ListingType,
} from "@/lib/listings";
import { SEO } from "@/lib/seo";

type PublicRequest = {
  id: string;
  apartment_type: ApartmentType;
  apartment_type_note: string | null;
  area: string;
  listing_type: ListingType;
  budget_min: number | null;
  budget_max: number;
  currency: CurrencyCode;
  created_at: string;
  notes: string | null;
};

const daysAgo = (iso: string) => {
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24)));
  return days;
};

const WantedProperties = () => {
  const { t, i18n } = useTranslation();
  const { buildWaUrl } = useWhatsApp();
  const [items, setItems] = useState<PublicRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [areaFilter, setAreaFilter] = useState("");
  const [areaChip, setAreaChip] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | ApartmentType>("all");
  const [listingTypeFilter, setListingTypeFilter] = useState<"all" | ListingType>("all");
  const [budgetMax, setBudgetMax] = useState("");
  const [period, setPeriod] = useState<Period>("all");
  const [sort, setSort] = useState<"newest" | "oldest" | "budgetAsc" | "budgetDesc">("newest");

  const hasActiveFilter =
    !!areaFilter || typeFilter !== "all" || listingTypeFilter !== "all" || !!budgetMax || period !== "all" || !!areaChip;

  const clearAll = () => {
    setAreaFilter("");
    setAreaChip("");
    setTypeFilter("all");
    setListingTypeFilter("all");
    setBudgetMax("");
    setPeriod("all");
    setSort("newest");
  };

  const isNew = (iso: string) => {
    const days = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
    return days < 7;
  };

  const areaChips = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of items) {
      counts.set(r.area, (counts.get(r.area) ?? 0) + 1);
    }
    const lang = i18n.language || "en";
    return Array.from(counts.entries())
      .map(([value, count]) => {
        const found = ISTANBUL_AREAS.find((a) => a.value === value);
        const label = found
          ? lang === "ar"
            ? found.labelAr
            : lang === "tr"
              ? found.labelTr
              : found.labelEn
          : value;
        return { value, label, count };
      })
      .sort((a, b) => b.count - a.count);
  }, [items, i18n.language]);

  useEffect(() => {
    const load = async () => {
      // public_requests view is anon-readable. Cast via any while types regenerate.
      const { data } = await (supabase as unknown as {
        from: (t: string) => { select: (s: string) => { order: (c: string, o: { ascending: boolean }) => Promise<{ data: PublicRequest[] | null }> } };
      })
        .from("public_requests")
        .select("*")
        .order("created_at", { ascending: false });
      setItems((data ?? []) as PublicRequest[]);
      setLoading(false);
    };
    void load();
  }, []);

  const filtered = useMemo(() => {
    const since = periodSince(period);
    const list = items.filter((r) => {
      if (areaFilter && !r.area.toLowerCase().includes(areaFilter.toLowerCase())) return false;
      if (areaChip && r.area !== areaChip) return false;
      if (typeFilter !== "all" && r.apartment_type !== typeFilter) return false;
      if (listingTypeFilter !== "all" && r.listing_type !== listingTypeFilter) return false;
      if (budgetMax && Number(r.budget_max) > Number(budgetMax)) return false;
      if (since && new Date(r.created_at) < since) return false;
      return true;
    });
    return [...list].sort((a, b) => {
      if (sort === "oldest") return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sort === "budgetAsc") return Number(a.budget_max) - Number(b.budget_max);
      if (sort === "budgetDesc") return Number(b.budget_max) - Number(a.budget_max);
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [items, areaFilter, areaChip, typeFilter, listingTypeFilter, budgetMax, period, sort]);

  const buildMatchMessage = (r: PublicRequest) => {
    const ago = daysAgo(r.created_at);
    return (
      `${t("wanted.match.intro")}\n\n` +
      `${t("wanted.match.refId")}: #${r.id.slice(0, 8)}\n` +
      `${t("wanted.card.type")}: ${formatApartmentType(r.apartment_type)} · ${formatRequestListingType(r.listing_type)}\n` +
      `${t("wanted.card.area")}: ${r.area}\n` +
      `${t("wanted.card.budget")}: ${(() => {
        const min = Number(r.budget_min) || 0;
        const max = Number(r.budget_max) || 0;
        if (min <= 0 && max <= 0) return t("wanted.card.budgetNotSpecified");
        if (min > 0 && max > 0) return `${formatPrice(min, r.currency)} - ${formatPrice(max, r.currency)}`;
        return formatPrice(max > 0 ? max : min, r.currency);
      })()}\n` +

      `${t("wanted.card.submittedAgo", { days: ago })}\n\n` +
      `${t("wanted.match.myProperty")}:\n`
    );
  };

  return (
    <>
      <SEO
        title={t("wanted.seo.title")}
        description={t("wanted.seo.description")}
        path="/wanted"
      />
      <section className="bg-gold-gradient/10 border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-black tracking-tight text-foreground sm:text-4xl">{t("wanted.title")}</h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">{t("wanted.hero.subtitle")}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{t("wanted.count", { n: filtered.length })}</p>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-xs font-bold text-foreground hover:bg-secondary"
          >
            <Filter className="h-3.5 w-3.5" /> {filtersOpen ? t("listings.hideFilters") : t("listings.showFilters")}
          </button>
        </div>

        {filtersOpen && (
          <div className="mb-6 grid gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                placeholder={t("wanted.filter.area")}
                className="w-full rounded-md border border-border bg-background py-2 ps-9 pe-3 text-sm"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as "all" | ApartmentType)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="all">{t("wanted.filter.type")}</option>
              {apartmentTypeOptions.map((a) => (
                <option key={a} value={a}>{formatApartmentType(a)}</option>
              ))}
            </select>
            <select
              value={listingTypeFilter}
              onChange={(e) => setListingTypeFilter(e.target.value as "all" | ListingType)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="all">{t("wanted.filter.listingType")}</option>
              {listingTypeOptions.map((l) => (
                <option key={l} value={l}>{formatListingType(l)}</option>
              ))}
            </select>
            <input
              value={budgetMax}
              onChange={(e) => setBudgetMax(e.target.value.replace(/[^\d]/g, ""))}
              inputMode="numeric"
              placeholder={t("wanted.filter.budget")}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
        )}

        {areaChips.length > 0 && (
          <div className="mb-6 flex flex-nowrap gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button
              type="button"
              onClick={() => setAreaChip("")}
              aria-pressed={areaChip === ""}
              className={`inline-flex shrink-0 items-center rounded-full border px-4 py-1.5 text-sm font-bold transition ${
                areaChip === ""
                  ? "border-primary bg-primary text-primary-foreground shadow"
                  : "border-border bg-card text-foreground hover:border-primary"
              }`}
            >
              {t("wanted.areaAll")}
            </button>
            {areaChips.map((c) => {
              const active = areaChip === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setAreaChip(active ? "" : c.value)}
                  aria-pressed={active}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-4 py-1.5 text-sm font-bold transition ${
                    active
                      ? "border-primary bg-primary text-primary-foreground shadow"
                      : "border-border bg-card text-foreground hover:border-primary"
                  }`}
                >
                  {c.label} <span className="opacity-80">{c.count}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <DateFilterPills value={period} onChange={setPeriod} />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as typeof sort)}
            aria-label={t("listings.filters.sortLabel")}
            className="min-h-[44px] rounded-md border border-border bg-background px-3 py-2 text-sm md:ms-auto"
          >
            <option value="newest">{t("listings.filters.sortNewest")}</option>
            <option value="oldest">{t("listings.filters.sortOldest")}</option>
            <option value="budgetAsc">{t("listings.filters.sortPriceAsc")}</option>
            <option value="budgetDesc">{t("listings.filters.sortPriceDesc")}</option>
          </select>
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearAll}
              className="min-h-[44px] rounded-full border border-border bg-card px-4 py-2 text-sm font-bold text-foreground hover:border-primary"
            >
              {t("wanted.clear")}
            </button>
          )}
        </div>

        {loading ? (
          <ListingGridSkeleton count={6} />
        ) : filtered.length === 0 ? (
          hasActiveFilter ? (
            <EmptyState icon={SearchX} title={t("wanted.noMatch")} ctaLabel={t("wanted.clear")} ctaOnClick={clearAll} />
          ) : (
            <EmptyState icon={Inbox} title={t("wanted.empty")} ctaLabel={t("home.ctaSubmit")} ctaTo="/submit" />
          )
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => {
              const waMsg = buildMatchMessage(r);
              const href = buildWaUrl(waMsg);
              return (
                <article key={r.id} className="flex flex-col rounded-xl border border-border bg-card p-5 shadow-soft transition hover:shadow-premium">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                      {formatRequestListingType(r.listing_type)}
                    </span>
                    {isNew(r.created_at) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-bold text-accent">
                        <Sparkles className="h-3 w-3" />
                        {t("wanted.newBadge")}
                      </span>
                    )}
                  </div>

                  <h3 className="mb-3 text-lg font-black text-foreground">
                    {formatApartmentType(r.apartment_type)}
                    {r.apartment_type === "other" && r.apartment_type_note ? ` · ${r.apartment_type_note}` : ""}
                  </h3>

                  <ul className="mb-4 space-y-2 text-sm text-foreground/90">
                    <li className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span><span className="text-xs text-muted-foreground">{t("wanted.card.area")}: </span>{r.area}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <DollarSign className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>
                        <span className="text-xs text-muted-foreground">{t("wanted.card.budget")}: </span>
                        {(() => {
                          const min = Number(r.budget_min) || 0;
                          const max = Number(r.budget_max) || 0;
                          if (min <= 0 && max <= 0) return t("wanted.card.budgetNotSpecified");
                          if (min > 0 && max > 0) return `${formatPrice(min, r.currency)} – ${formatPrice(max, r.currency)}`;
                          return formatPrice(max > 0 ? max : min, r.currency);
                        })()}
                      </span>
                    </li>

                  </ul>

                  {r.notes && r.notes.trim() && (
                    <div className="mb-4 -mt-1 flex items-start gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
                      <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
                      <p className="line-clamp-3 whitespace-pre-wrap">
                        <span className="font-bold text-foreground/80">{t("wanted.card.notesLabel")}: </span>
                        {r.notes}
                      </p>
                    </div>
                  )}


                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-success px-4 py-2.5 text-sm font-bold text-primary-foreground transition hover:scale-[1.02]"
                  >
                    <MessageCircle className="h-4 w-4" /> {t("wanted.card.matchButton")}
                  </a>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
};

export default WantedProperties;
